/**
 * Identity / context the survey was opened with — typically passed on
 * the URL when Property Meld punches a customer into the survey, e.g.
 *
 *   /?mid=abc123&email=erin@acme.com&company=Acme%20PM
 *
 * When `mid` and `email` are both present we treat the respondent as
 * known and skip the intro form. When either is missing the intro
 * step asks for company + email + a follow-up checkbox and writes the
 * answers back into this same object.
 *
 * Persisted in sessionStorage so query params survive client-side
 * route changes (admin → home, post-submit thanks page).
 */

export interface Punchout {
  mid: string;
  user_id: string;
  user_name: string;
  user_email: string;
  company_id: string;
  company: string;
  openToFollowup: boolean;
}

export const EMPTY_PUNCHOUT: Punchout = {
  mid: "",
  user_id: "",
  user_name: "",
  user_email: "",
  company_id: "",
  company: "",
  openToFollowup: false,
};

const STORAGE_KEY = "truecost-survey:punchout";

export function readPunchoutFromUrl(): Punchout {
  const params = new URLSearchParams(window.location.search);
  return {
    mid:           params.get("mid") || params.get("management_id") || "",
    user_id:       params.get("user_id") || params.get("uid") || "",
    user_name:     params.get("user") || params.get("user_name") || params.get("name") || "",
    user_email:    params.get("user_email") || params.get("email") || "",
    company_id:    params.get("company_id") || params.get("cid") || "",
    company:       params.get("company") || params.get("company_name") || "",
    openToFollowup: false,
  };
}

/**
 * Whether the URL gave us enough identity to skip the intro form.
 * Both `mid` and `user_email` must be present.
 */
export function hasPunchoutIdentity(p: Punchout): boolean {
  return Boolean(p.mid && p.user_email);
}

/**
 * Whether the respondent has supplied enough information to advance
 * past the intro step:
 *   - name is always required
 *   - company is required (URL value satisfies it; otherwise ask)
 *   - email is required only when the respondent opts in to a
 *     follow-up conversation
 */
export function canAdvanceFromIntro(p: Punchout): boolean {
  if (!p.user_name.trim()) return false;
  if (!p.company.trim()) return false;
  if (p.openToFollowup && !p.user_email.trim()) return false;
  return true;
}

/**
 * Capture punchout from URL on first load and stash it in
 * sessionStorage so query params survive client-side navigation.
 */
export function loadPunchout(): Punchout {
  const fromUrl = readPunchoutFromUrl();
  const hasAny = Object.values(fromUrl).some(Boolean);
  if (hasAny) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    try { return { ...EMPTY_PUNCHOUT, ...JSON.parse(stored) }; }
    catch { /* fall through */ }
  }
  return EMPTY_PUNCHOUT;
}

export function savePunchout(p: Punchout): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}
